import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signingAPI } from '../services/api';
import Button from './ui/Button';
import Input from './ui/Input';
import Alert from './ui/Alert';
import { useToast } from '../context/ToastContext';

// Activarea identității de semnătură electronică avansată (AdES) pentru userul curent.
// Apare la onboarding după ce userul are CNP setat + parolă schimbată, sau în profil pentru
// utilizatorii existenți care nu au încă o cheie. Cere parola pentru a securiza cheia privată.
const SigningKeyActivationModal = ({ onSuccess, onClose, allowClose = false }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!password) {
      setError(t('modals.signingKey.passwordRequired'));
      return;
    }

    setError(null);
    setLoading(true);
    try {
      await signingAPI.generate(password);
      showToast(t('modals.signingKey.success'), 'success');
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || t('toasts.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-md">
        <div className="modal-header">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary-100 dark:bg-primary-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-primary-600 dark:text-primary-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {t('modals.signingKey.title')}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('modals.signingKey.subtitle')}
              </p>
            </div>
          </div>
        </div>

        <div className="modal-body">
          <div className="mb-4 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg text-sm text-slate-700 dark:text-slate-300">
            {t('modals.signingKey.explanation')}
          </div>

          {error && (
            <Alert type="error" className="mb-3">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} id="signing-key-form">
            <Input
              type="password"
              label={t('modals.signingKey.passwordLabel')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              autoFocus
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
              {t('modals.signingKey.passwordHint')}
            </p>
          </form>
        </div>

        <div className="modal-footer flex gap-2">
          {allowClose && (
            <Button variant="ghost" onClick={onClose} disabled={loading}>
              {t('common.later')}
            </Button>
          )}
          <Button
            type="submit"
            form="signing-key-form"
            loading={loading}
            disabled={!password || loading}
            className="flex-1"
          >
            {loading ? t('common.loading') : t('modals.signingKey.activateBtn')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SigningKeyActivationModal;
